import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { EnvironmentService } from '@app/core/services/environment.service';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { TelerikScriptLoaderService } from '../../services/telerik-script-loader.service';

declare const $: any;

@Component({
    selector: 'app-report-viewer',
    templateUrl: './report-viewer.component.html',
    styleUrls: ['./report-viewer.component.scss']
})
export class ReportViewerComponent implements OnInit, OnDestroy {
    public reportRunId: string;
    public errorMessage: string | null = null;

    private viewer: any;
    private originalFetch: typeof window.fetch | null = null;

    constructor(
        private envService: EnvironmentService,
        private route: ActivatedRoute,
        private location: Location,
        private oidcAuthService: OidcAuthService,
        private scriptLoader: TelerikScriptLoaderService,
    ) { }

    ngOnInit(): void {
        this.reportRunId = this.route.snapshot.paramMap.get('runId');

        // The viewer's own bootstrap calls are plain window.fetch calls, same as the designer -
        // see ReportDesignerComponent.overrideFetchForDesigner() for why this is necessary.
        this.overrideFetchForViewer();

        this.loadScripts()
            .then(() => this.initializeViewer())
            .catch(() => {
                this.errorMessage = 'The report viewer could not be loaded.';
            });
    }

    private initializeViewer(): void {
        const viewerElement = document.getElementById('reportViewer');
        if (viewerElement) {
            viewerElement.innerHTML = '';
        }

        const onViewerInitializing = (e: any, args: any) => {
            args.reportViewerOptions.authenticationToken = this.oidcAuthService.authorizationHeaderValue;
        };

        // ReportRunSourceResolver resolves this id against completed ReportRuns first, so no
        // file extension is needed here (unlike the designer's .trdp report uris).
        // The widget's jQuery plugin is registered as telerik_ReportViewer (matching the
        // telerik_WebReportDesigner naming used by the designer), not telerikReportViewer.
        this.viewer = $('#reportViewer').telerik_ReportViewer({
            serviceUrl: `${this.envService.env.apiUrl}Reports/Viewer/`,
            reportSource: { report: this.reportRunId },
            viewerInitializing: onViewerInitializing,
            // This viewer only ever displays an already-completed ReportRun - its parameter
            // values were fixed when the run was submitted, so showing a live, editable
            // parameters panel here would let a user re-submit different values and trigger
            // another server round trip (re-resolving the source, re-reading the cached data)
            // for a result that's already final. Hide it entirely.
            parametersAreaVisible: false
        }).data('telerik_ReportViewer');
    }

    ngOnDestroy(): void {
        if (this.viewer && typeof this.viewer.destroy === 'function') {
            this.viewer.destroy();
        }

        const viewerElement = document.getElementById('reportViewer');
        if (viewerElement) {
            viewerElement.innerHTML = '';
        }

        this.restoreFetch();
    }

    private overrideFetchForViewer(): void {
        const originalFetch = window.fetch.bind(window);
        this.originalFetch = originalFetch;

        const serviceUrl = `${this.envService.env.apiUrl}Reports/Viewer/`;

        window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
            const url = typeof input === 'string' ? input : input.toString();

            if (url.startsWith(serviceUrl)) {
                const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
                headers.set('Authorization', this.oidcAuthService.authorizationHeaderValue);
                headers.set('TenantId', this.oidcAuthService.getTenantId());
                init = { ...init, headers };
            }

            return originalFetch(input, init);
        };
    }

    private restoreFetch(): void {
        if (this.originalFetch) {
            window.fetch = this.originalFetch;
            this.originalFetch = null;
        }
    }

    goBack(): void {
        this.location.back();
    }

    private loadScripts(): Promise<void> {
        return this.scriptLoader.loadScript('https://code.jquery.com/jquery-3.7.1.min.js')
            .then(() => {
                this.scriptLoader.loadStyle('https://code.jquery.com/ui/1.14.1/themes/base/jquery-ui.min.css');
                return this.scriptLoader.loadScript('https://code.jquery.com/ui/1.14.1/jquery-ui.min.js');
            })
            .then(() => {
                this.scriptLoader.loadStyle('https://reporting.cdn.telerik.com/20.2.26.812/styles/telerikReportViewer.min.css');
            })
            .then(() => this.scriptLoader.loadScript('https://reporting.cdn.telerik.com/20.2.26.812/js/webReportDesigner.kendo.min.js'))
            .then(() => this.scriptLoader.loadScript('https://reporting.cdn.telerik.com/20.2.26.812/js/telerikReportViewer.min.js'));
    }
}
